import React from "react";
import styled from "styled-components/macro";
import Action from "../../components/Action";
import Card from "../../components/DashboardCard";
import Confirmation from "../../components/Confirmation";
import Image from "../../components/Image";
import imageOfficeWorking from "../../images/office-working.png";
import PageHeader from "../../components/PageHeader";
import QuestionDescription from "../../components/QuestionDescription";
import ReviewText from "../../components/ReviewText";
import ScrollToTop from "../../components/utils/ScrollToTop";
import useDynamicFormDoable from "../../hooks/useDynamicFormDoable";
import { sanitiseHtml } from "../../utils/sanitiseHtml";
import DynamicFormLoader, { formatSavedAt } from "./dynamicForm/DynamicFormLoader";
import FormQuestions from "./dynamicForm/FormQuestions";
import ReviewAnswers from "./dynamicForm/ReviewAnswers";

const DraftSavedNote = styled.p`
  color: var(--juno-color-text-secondary);
  font-size: var(--juno-font-size-body-sm);
`;

const DynamicForm = ({
  doable,
  configuration,
  initialDocumentUploads,
  postal2FACode,
  caseRef,
  returnToDashboard,
  savedAt,
  saveDraft,
  cancelPendingSave,
}) => {
  const form = useDynamicFormDoable({
    doable,
    configuration,
    initialDocumentUploads,
    postal2FACode,
    saveDraft,
    cancelPendingSave,
  });
  const {
    reviewing,
    reviewResponses,
    submitted,
    submissionError,
    isDuplicateSubmission,
    uploadInProgress,
    submitResponses,
    saveResponses,
  } = form;

  const createContent = (content) => {
    return { __html: sanitiseHtml(content) };
  };

  const requestTitle = doable.title;
  const requestDescription = doable.description;
  const checkBeforeSubmit = configuration.check_before_submit;
  const savedAtText = savedAt && formatSavedAt(savedAt);

  if (!!submissionError) {
    return (
      <Card title="Sorry, that didn’t work" removeTopPadding>
        {isDuplicateSubmission ? (
          <>
            <p>You’ve already submitted this form.</p>
            <Action small onClick={returnToDashboard}>
              Take me back to my dashboard
            </Action>
          </>
        ) : (
          <>
            <p>We’re having trouble submitting your answers at the moment.</p>
            <p>Please send us a message via web chat, and let us know you were filling in the form:</p>
            <p>
              <em>{requestTitle}</em>
            </p>
          </>
        )}
      </Card>
    );
  }

  if (submitted) {
    return (
      <>
        <Confirmation title="Thanks! We’ve got your responses">
          <p>As soon as we need anything else we’ll drop you an email with a link to your dashboard.</p>
          <Action small onClick={returnToDashboard}>
            Take me back to my dashboard
          </Action>
        </Confirmation>
        <Image src={imageOfficeWorking} fullWidth={true} />
      </>
    );
  }

  if (reviewing) {
    return (
      <>
        <PageHeader>Review your answers</PageHeader>
        <Card title={requestTitle} removeTopPadding>
          {requestDescription && <QuestionDescription dangerouslySetInnerHTML={createContent(requestDescription)} />}
          <ReviewText>
            We’ve listed your answers below — if anything doesn’t look right, you can change your answers before you
            submit.
          </ReviewText>
          <ReviewAnswers configuration={configuration} form={form} />
          <Action small onClick={submitResponses}>
            Submit your answers
          </Action>
        </Card>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <PageHeader>Answer questions</PageHeader>
      <Card title={requestTitle} removeTopPadding>
        {requestDescription && <QuestionDescription dangerouslySetInnerHTML={createContent(requestDescription)} />}
        {savedAtText && <DraftSavedNote>Your answers save automatically — last saved {savedAtText}.</DraftSavedNote>}
        <React.Fragment>
          <FormQuestions configuration={configuration} form={form} caseRef={caseRef} doableId={doable.id} />

          {checkBeforeSubmit ? (
            <Action small disabled={uploadInProgress} onClick={reviewResponses}>
              Continue
            </Action>
          ) : (
            <Action small disabled={uploadInProgress} onClick={saveResponses}>
              Save your answers
            </Action>
          )}
        </React.Fragment>
      </Card>
    </>
  );
};

const DynamicFormDoable = (props) => <DynamicFormLoader {...props} Form={DynamicForm} />;

export default DynamicFormDoable;
